import React from 'react';
import { downloadDashboardExport } from '../../services/manualDashboardService';
import { cohortOptions } from './format';
import { useSectionGrain } from './useSectionGrain';
import type { ManualDashboardPayload } from '../../types/manualDashboard';
import type { DateRange } from '../../ui-kit';

export interface TableSectionParams {
  dashboardId?: string;
  blockId: string;
  payload: ManualDashboardPayload;
  range: DateRange;
  filters: Record<string, string[]>;
  refreshNonce: number;
  /** Told whenever this table's grouping differs from the board's (or stops differing). */
  onGrainChange?: (blockId: string, grain: string | undefined) => void;
  /** Only these groupings are offered, when the table cannot be drawn at every one. */
  allowedGrains?: string[];
}

/**
 * What every table on the board owns: its own cohort grouping, and an export
 * of itself as shown.
 *
 * The grouping resets when the range changes, because a new range offers
 * different groupings. `source` is the payload to read this table from — the
 * board's own, or a regrouped one fetched for this table alone.
 */
export function useTableSection({
  dashboardId,
  blockId,
  payload,
  range,
  filters,
  refreshNonce,
  onGrainChange,
  allowedGrains,
}: TableSectionParams) {
  const [chosen, setChosen] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    setChosen(undefined);
  }, [range]);

  const options = cohortOptions(payload).filter((o) => !allowedGrains || allowedGrains.includes(o.value));
  const offered = chosen && options.some((o) => o.value === chosen) ? chosen : undefined;
  const grain = offered ?? payload.grain;

  React.useEffect(() => {
    onGrainChange?.(blockId, grain === payload.grain ? undefined : grain);
  }, [blockId, grain, payload.grain, onGrainChange]);

  const override = useSectionGrain({
    dashboardId, grain, boardGrain: payload.grain, range, filters, refreshNonce,
  });

  // Exports this table as it is on screen — its grouping, the range and the
  // filters — so the file and the page cannot disagree.
  const [downloading, setDownloading] = React.useState(false);
  const [exportError, setExportError] = React.useState<string | null>(null);
  const exportTable = async () => {
    if (!dashboardId) return;
    setDownloading(true);
    setExportError(null);
    try {
      await downloadDashboardExport(dashboardId, {
        grain, filters, start: range.start, end: range.end, block: blockId,
      });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Could not export this table');
    } finally {
      setDownloading(false);
    }
  };

  const problem = exportError
    ? `Could not export this table: ${exportError}`
    : override.error ? `Could not regroup this table: ${override.error}` : null;

  return {
    grain,
    options,
    setGrain: setChosen,
    source: override.payload ?? payload,
    regrouping: override.loading,
    downloading,
    exportTable,
    problem,
  };
}

export type TableSection = ReturnType<typeof useTableSection>;
